import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import { lazy, Suspense, useEffect, useState } from "react";
import { Toaster } from "sonner";
import { api } from "./lib/api";
import { AuthProvider } from "./context/AuthContext";
import { RequireAuth } from "./components/Guards";
import { PatternDefs } from "./components/Illustration";
import { Loader } from "./components/Bits";
import { MobileBar } from "./components/MobileBar";
import Home from "./pages/Home";
import AllProposals from "./pages/AllProposals";
import ProposalPage from "./pages/ProposalPage";

// Pages most visitors never open load on demand, keeping the first download small.
const NewProposal = lazy(() => import("./pages/NewProposal"));
const Builder = lazy(() => import("./pages/Builder"));
const AuthPage = lazy(() => import("./pages/AuthPage"));
const ResetPassword = lazy(() => import("./pages/ResetPassword"));
const Verify = lazy(() => import("./pages/Verify"));
const MyProposals = lazy(() => import("./pages/dashboard/MyProposals"));
const Likes = lazy(() => import("./pages/dashboard/Likes"));
const Profile = lazy(() => import("./pages/dashboard/Profile"));
const AdminProposals = lazy(() => import("./pages/admin/AdminProposals"));
const AdminReview = lazy(() => import("./pages/admin/AdminReview"));
const AdminUsers = lazy(() => import("./pages/admin/AdminUsers"));
const AdminUserDetail = lazy(() => import("./pages/admin/AdminUserDetail"));
const AdminTopics = lazy(() => import("./pages/admin/AdminTopics"));
const AdminLog = lazy(() => import("./pages/admin/AdminLog"));
const AdminAccess = lazy(() => import("./pages/admin/AdminAccess"));
const AccessGate = lazy(() => import("./pages/AccessGate"));
const staticPage = (name) => lazy(() => import("./pages/Static").then((m) => ({ default: m[name] })));
const About = staticPage("About");
const Contact = staticPage("Contact");
const Guidelines = staticPage("Guidelines");
const NotFound = staticPage("NotFound");
const Privacy = staticPage("Privacy");
const Report = staticPage("Report");

const ScrollTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

// Keeps ?next=… when the old /login address is used.
const LoginRedirect = () => <Navigate to={`/signin${useLocation().search}`} replace />;

const user = (el) => <RequireAuth>{el}</RequireAuth>;
const admin = (el) => <RequireAuth admin>{el}</RequireAuth>;

// Private preview: the gate is checked once before the app mounts, and again if any call comes back "site_locked".
function useSiteGate() {
  const [locked, setLocked] = useState(null);
  useEffect(() => {
    api.get("/access").then(({ data }) => setLocked(data.locked && !data.ok)).catch(() => setLocked(false));
    const onLocked = () => setLocked(true);
    window.addEventListener("site-locked", onLocked);
    return () => window.removeEventListener("site-locked", onLocked);
  }, []);
  return [locked, () => window.location.reload()];
}

export default function App() {
  const [locked, unlocked] = useSiteGate();
  if (locked === null) return <Loader />;
  if (locked) return <Suspense fallback={<Loader />}><AccessGate onUnlocked={unlocked} /></Suspense>;
  return (
    <AuthProvider>
      <BrowserRouter>
        <ScrollTop />
        <PatternDefs />
        <Suspense fallback={<Loader />}>
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/proposals" element={<AllProposals />} />
          <Route path="/proposal/:slug" element={<ProposalPage />} />
          <Route path="/new" element={user(<NewProposal />)} />
          <Route path="/builder/:id" element={user(<Builder />)} />
          <Route path="/signin" element={<AuthPage />} />
          <Route path="/signup" element={<AuthPage />} />
          <Route path="/login" element={<LoginRedirect />} />
          <Route path="/lander" element={<Navigate to="/" replace />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/verify" element={<Verify />} />
          <Route path="/dashboard" element={user(<MyProposals />)} />
          <Route path="/dashboard/likes" element={user(<Likes />)} />
          <Route path="/dashboard/profile" element={user(<Profile />)} />
          <Route path="/admin" element={admin(<AdminProposals />)} />
          <Route path="/admin/proposals/:id" element={admin(<AdminReview />)} />
          <Route path="/admin/users" element={admin(<AdminUsers />)} />
          <Route path="/admin/users/:id" element={admin(<AdminUserDetail />)} />
          <Route path="/admin/topics" element={admin(<AdminTopics />)} />
          <Route path="/admin/log" element={admin(<AdminLog />)} />
          <Route path="/admin/access" element={admin(<AdminAccess />)} />
          <Route path="/about" element={<About />} />
          <Route path="/guidelines" element={<Guidelines />} />
          <Route path="/privacy" element={<Privacy />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/report" element={<Report />} />
          <Route path="*" element={<NotFound />} />
        </Routes>
        </Suspense>
        <MobileBar />
      </BrowserRouter>
      <Toaster position="top-center" richColors closeButton />
    </AuthProvider>
  );
}
